/**
 * O anúncio de início de fase.
 *
 * Nos Fire Emblem de GBA a virada de turno não é uma linha discreta no canto:
 * a tela escurece, uma faixa atravessa o campo dizendo de quem é a vez, e só
 * então o jogo devolve o controle. Sem esse respiro é fácil mexer achando que
 * ainda é sua vez — sobretudo contra a CPU, que começa a agir sozinha.
 */

import { useEffect, useRef, useState } from 'react';
import type { Team } from '@oz/rules';
import type { GameAudio } from './audio.js';

const TITLE: Partial<Record<Team, string>> = {
  jogador: 'SUA VEZ',
  inimigo: 'VEZ DO INIMIGO',
  aliado: 'VEZ DOS ALIADOS',
  neutro: 'VEZ DOS NEUTROS',
};

const SUBTITLE: Partial<Record<Team, string>> = {
  jogador: 'mova seu esquadrão',
  inimigo: 'as forças inimigas avançam',
  aliado: 'seus aliados agem sozinhos',
  neutro: 'movimentação neutra',
};

/** Quanto a faixa fica na tela, em milissegundos. */
const DURATION_MS = 2700;

export function PhaseBanner({
  phase,
  turn,
  active,
  onDone,
  audio,
}: {
  readonly phase: Team;
  readonly turn: number;
  /** Falso quando a partida acabou: não se anuncia turno de jogo encerrado. */
  readonly active: boolean;
  readonly onDone?: () => void;
  readonly audio?: GameAudio | null;
}): JSX.Element | null {
  const [shown, setShown] = useState<{ phase: Team; turn: number } | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!active) return;
    setShown({ phase, turn });
    audio?.play('turno');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const id = window.setTimeout(
      () => {
        setShown(null);
        onDoneRef.current?.();
      },
      reduced ? 400 : DURATION_MS,
    );
    return () => window.clearTimeout(id);
    // De propósito só `phase` e `turn`: a faixa existe para marcar a virada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, turn, active]);

  if (!shown) return null;

  return (
    <div className={`phase-banner phase-banner-${shown.phase}`} role="status" aria-live="polite">
      {/* A faixa é irmã do texto, mas mora dentro da mesma tira: assim ela tem
          exatamente a altura das três linhas mais o respiro, em vez de uma
          altura fixa que o título transbordava por cima e por baixo. */}
      <div className="phase-banner-strip">
        <div className="phase-banner-sweep" />
        <div className="phase-banner-text">
          <span className="phase-banner-turn">Turno {shown.turn}</span>
          <strong className="phase-banner-title">{TITLE[shown.phase] ?? shown.phase}</strong>
          <span className="phase-banner-sub">{SUBTITLE[shown.phase] ?? ''}</span>
        </div>
      </div>
    </div>
  );
}
